import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import ResearchLayout from '../../components/ResearchLayout';
import Pagination from '../../components/Pagination';
import api from '../../services/api';  // Assuming your API service is here
import '../../assets/css/Research.css';

const ProgramAdminProtocols = () => {
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answerStatuses, setAnswerStatuses] = useState({});  // Track if questions have answers for this protocol
  const location = useLocation();

  useEffect(() => {
    loadProtocols();
    
    // Check for success message from redirect (e.g., after editing)
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      setShowSuccess(true);
      
      // Clear the message from location state after displaying
      window.history.replaceState({}, document.title);
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    }
  }, [location]);

  const loadProtocols = async () => {
    try {
      setLoading(true);
      // Request all protocols (up to 1000) to fix missing protocols issue
      const response = await api.get('/protocols?page=1&per_page=1000');
      if (response && response.data && response.data.protocols) {
        // Add this debug log
        console.log('Protocol data received:', response.data.protocols[0]);
        console.log('Total protocols loaded:', response.data.protocols.length);
        setProtocols(response.data.protocols);
      } else {
        throw new Error('Failed to load protocols');
      }
    } catch (error) {
      console.error('Error loading protocols:', error);
      setError(error.message || 'Failed to load protocols');
    } finally {
      setLoading(false);
    }
  };

  // Filter protocols based on search term and active/inactive filter
  const filteredProtocols = protocols.filter(protocol => {
    const matchesSearch = protocol.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         protocol.description.toLowerCase().includes(searchTerm.toLowerCase());
    if (filter === 'all') return matchesSearch;
    if (filter === 'active') return matchesSearch && protocol.is_active;
    if (filter === 'inactive') return matchesSearch && !protocol.is_active;
    return matchesSearch;
  });

  // Pagination logic (match cases pagination, 15 per page)
  const [currentPage, setCurrentPage] = useState(1);
  const protocolsPerPage = 15;
  const totalPages = Math.ceil(filteredProtocols.length / protocolsPerPage);
  const paginatedProtocols = filteredProtocols.slice((currentPage - 1) * protocolsPerPage, currentPage * protocolsPerPage);

  // Go to page handler (like cases)
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Reset to first page when filters/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filter, protocols]);

  // Add this function above the return statement
  const getQuestionsCount = (protocol) => {
    // Try multiple possible property names for questions count
    return protocol.questions_count || 
           protocol.questionsCount || 
           protocol.question_count || 
           protocol.questionCount || 
           (protocol.questions && protocol.questions.length) || 
           0;
  };

  // When a protocol is selected, fetch its questions and check answer statuses
  const handleProtocolSelect = async (protocolId) => {
    setSelectedProtocol(protocolId);
    try {
      const questionsResponse = await api.get(`/protocols/${protocolId}/questions`);
      setQuestions(questionsResponse.data.questions);
      
      const statuses = {};
      for (const question of questionsResponse.data.questions) {
        try {
          const answerResponse = await api.get(`/answers?protocol_id=${protocolId}&question_id=${question.id}`);
          statuses[question.id] = answerResponse.data.answers.length > 0;
          console.log(`DEBUG: Answer status for question ${question.id}: ${statuses[question.id]}`);  // Add debug log
        } catch (error) {
          console.error(`Error fetching answers for question ${question.id}:`, error);
          statuses[question.id] = false;  // Default to false on error
        }
      }
      setAnswerStatuses(statuses);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setError('Failed to load questions for this protocol');
    }
  };

  // Handle unlinking a question
  const handleUnlinkQuestion = async (questionId) => {
    console.log(`DEBUG: Attempting to unlink question ${questionId}, status: ${answerStatuses[questionId]}`);  // Add debug log

    if (answerStatuses[questionId]) {
      alert('Cannot unlink question. It has already been answered by a doctor for this protocol.');
      return;
    }

    try {
      const response = await api.delete(`/protocols/${selectedProtocol}/questions/${questionId}`);
      // Backend may return JSON with rows_deleted/debug
      const data = response.data || {};
      const rowsDeleted = data.rows_deleted ?? data.rowsDeleted ?? data.rows_deleted === 0 ? data.rows_deleted : null;
      const debug = data.debug || {};

      if (typeof rowsDeleted === 'number' ? rowsDeleted > 0 : (data.success || response.status === 200)) {
        alert('Question unlinked successfully');
        handleProtocolSelect(selectedProtocol);  // Refresh
      } else if (data.error) {
        alert('Cannot unlink: ' + data.error);
      } else {
        alert('No changes made. Question may already be unlinked.');
        handleProtocolSelect(selectedProtocol);
      }
    } catch (error) {
      console.error('Error unlinking question:', error);
      if (error.response && error.response.status === 400) {
        alert('Cannot unlink: ' + error.response.data.error);  // Show backend error
      } else {
        alert('Error unlinking question: ' + (error.response?.data?.error || 'Unknown error'));
      }
    }
  };


  return (
    <ResearchLayout pageTitle="Research Protocols">
      <div className="container-fluid">
        {/* Success Alert */}
        {showSuccess && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {successMessage || 'Operation completed successfully.'}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setShowSuccess(false)}
            ></button>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setError('')}
            ></button>
          </div>
        )}

        {/* Header with Actions */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-6 mb-3 mb-md-0">
            <h2 className="mb-0">
              <i className="fas fa-clipboard-list text-primary me-2"></i>
              Research Protocols
            </h2>
          </div>
          <div className="col-md-6 text-md-end">
            <Link to="/program-admin/create-protocol" className="btn btn-primary">
              <i className="fas fa-plus me-2"></i>
              Create New Protocol
            </Link>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-8">
                <div className="input-group">
                  <span className="input-group-text bg-light">
                    <i className="fas fa-search"></i>
                  </span>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Search protocols by name or description..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={() => setSearchTerm('')}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <select 
                  className="form-select" 
                  value={filter} 
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="all">All Protocols</option>
                  <option value="active">Active Protocols</option>
                  <option value="inactive">Inactive Protocols</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Protocols List */}
        <div className="row">
          <div className="col-12">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading protocols...</span>
                </div>
                <p className="mt-2">Loading protocols...</p>
              </div>
            ) : filteredProtocols.length > 0 ? (
              <div className="card border-0 shadow-sm">
                {/* Desktop Table */}
                <div className="table-responsive d-none d-md-block">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Questions</th>
                        <th>AI Response</th>
                        <th>Active</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedProtocols.map(protocol => (
                        <tr key={protocol.id}>
                          <td className="fw-medium">
                            <Link to={`/program-admin/view-protocol/${protocol.id}`} className="text-decoration-none">
                              {protocol.name}
                            </Link>
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '250px' }}>
                              {protocol.description}
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark">
                              {getQuestionsCount(protocol)} Questions
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${protocol.show_ai_response ? 'bg-info' : 'bg-secondary'}`}
                              title={protocol.show_ai_response ? 'Doctors can view AI assessment' : 'Doctors cannot view AI assessment'}>
                              <i className="fas fa-robot me-1"></i>
                              {protocol.show_ai_response ? 'AI Viewed' : 'AI Hidden'}
                            </span>
                          </td>
                          <td>
                            {protocol.is_active ? (
                              <span className="badge bg-success">Active</span>
                            ) : (
                              <span className="badge bg-secondary">Inactive</span>
                            )}
                          </td>
                          <td>
                            <small className="text-muted">
                              {new Date(protocol.created_at).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            <div className="btn-group flex-wrap">
                              <Link
                                to={`/program-admin/edit-protocol/${protocol.id}`}
                                className="btn btn-sm btn-outline-primary"
                              >
                                <i className="fas fa-edit"></i> Edit
                              </Link>
                              <Link
                                to={`/program-admin/link-questions?protocol=${protocol.id}`}
                                className="btn btn-sm btn-outline-info"
                              >
                                <i className="fas fa-link"></i> Questions
                              </Link>
                              <Link
                                to={`/program-admin/view-protocol/${protocol.id}`}
                                className="btn btn-sm btn-outline-secondary"
                              >
                                <i className="fas fa-eye"></i> View
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile Cards */}
                <div className="d-block d-md-none">
                  {paginatedProtocols.map(protocol => (
                    <div key={protocol.id} className="card mb-3 border border-primary">
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6 className="mb-0 fw-bold text-primary">
                            <Link to={`/program-admin/view-protocol/${protocol.id}`} className="text-decoration-none">
                              {protocol.name}
                            </Link>
                          </h6>
                          <span className={`badge ${protocol.show_ai_response ? 'bg-info' : 'bg-secondary'}`}
                            title={protocol.show_ai_response ? 'Doctors can view AI assessment' : 'Doctors cannot view AI assessment'}>
                            <i className="fas fa-robot me-1"></i>
                            {protocol.show_ai_response ? 'AI Viewed' : 'AI Hidden'}
                          </span>
                        </div>
                        <div className="mb-2 text-muted small">
                          {protocol.description}
                        </div>
                        <div className="mb-2">
                          <span className="badge bg-light text-dark me-2">
                            {getQuestionsCount(protocol)} Questions
                          </span>
                          {protocol.is_active ? (
                            <span className="badge bg-success me-2">Active</span>
                          ) : (
                            <span className="badge bg-secondary me-2">Inactive</span>
                          )}
                          <span className="badge bg-light text-dark">
                            {new Date(protocol.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="btn-group w-100 flex-wrap">
                          <Link
                            to={`/program-admin/edit-protocol/${protocol.id}`}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="fas fa-edit"></i> Edit
                          </Link>
                          <Link
                            to={`/program-admin/link-questions?protocol=${protocol.id}`}
                            className="btn btn-sm btn-outline-info"
                          >
                            <i className="fas fa-link"></i> Questions
                          </Link>
                          <Link
                            to={`/program-admin/view-protocol/${protocol.id}`}
                            className="btn btn-sm btn-outline-secondary"
                          >
                            <i className="fas fa-eye"></i> View
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-5 bg-light rounded">
                <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                <h4>No protocols found</h4>
                <p className="text-muted">
                  {searchTerm ? 
                    'No protocols match your search criteria.' : 
                    'Get started by creating your first research protocol.'
                  }
                </p>
                <Link to="/program-admin/create-protocol" className="btn btn-primary mt-2">
                  <i className="fas fa-plus me-2"></i>
                  Create New Protocol
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Pagination Controls */}
        {filteredProtocols.length > protocolsPerPage && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
              showInfo={true}
              totalItems={filteredProtocols.length}
              itemsPerPage={protocolsPerPage}
            />
          </div>
        )}

        {/* Protocol Count Summary */}
        {filteredProtocols.length > 0 && (
          <div className="mt-4 text-center">
            <div className="badge bg-light text-dark p-2">
              Showing {paginatedProtocols.length} of {filteredProtocols.length} protocols on this page
              {filter !== 'all' && (
                <span> ({filter === 'active' ? 'active' : 'inactive'} only)</span>
              )}
              {searchTerm && (
                <span> matching "{searchTerm}"</span>
              )}
            </div>
          </div>
        )}

      </div>
    </ResearchLayout>
  );
};

export default ProgramAdminProtocols;